import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_700Bold } from '@expo-google-fonts/inter';
import { useSentimento } from '@/contexts/SentimentoContext';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import TertiaryButton from '@/components/TertiaryButton';
import { useLanguage } from '@/contexts/LanguageContext';

export default function DiarioSentimentoNotaScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const { emotion, categories, selectedDate } = useLocalSearchParams<{ emotion: string; categories: string; selectedDate: string }>();
  const [noteText, setNoteText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { createEntry } = useSentimento();

  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_700Bold,
    'Sentient-Regular': require('../assets/fonts/Sentient-Regular.otf'),
  });

  if (!fontsLoaded) {
    return null;
  }

  const getEmotionEmoji = (emotionId: string) => {
    const emotionMap: { [key: string]: string } = {
      'feliz': '😀',
      'animado': '🤩',
      'tranquilo': '😌',
      'cansado': '😴',
      'frustrado': '😣',
      'triste': '😭',
      'estressado': '😤',
      'irritado': '😠',
      'ansioso': '😰',
    };
    return emotionMap[emotionId] || '😐';
  };

  const getCategoryLabel = (categoryId: string | string[]) => {
    const categoryMap: { [key: string]: string } = {
      'vida-pessoal': t('diary.categories.vida-pessoal'),
      'trabalho': t('diary.categories.trabalho'),
      'relacionamentos': t('diary.categories.relacionamentos'),
      'saude': t('diary.categories.saude'),
      'financas': t('diary.categories.financas'),
      'sobriedade': t('diary.categories.sobriedade'),
      'lazer': t('diary.categories.lazer'),
      'espiritualidade': t('diary.categories.espiritualidade'),
      'educacao': t('diary.categories.educacao'),
      'outros': t('diary.categories.outros'),
    };

    if (Array.isArray(categoryId)) {
      const labels = categoryId.map(id => categoryMap[id] || id).filter(Boolean);
      if (labels.length === 0) return '';
      if (labels.length === 1) return labels[0];
      if (labels.length === 2) return `${labels[0]} ${t('common.and')} ${labels[1]}`;

      const lastLabel = labels.pop();
      return `${labels.join(', ')} ${t('common.and')} ${lastLabel}`;
    }

    // Handle comma-separated string
    if (typeof categoryId === 'string' && categoryId.includes(',')) {
      const categories = categoryId.split(',').map(c => c.trim());
      return getCategoryLabel(categories);
    }

    return categoryMap[categoryId] || '';
  };

  const handleSave = async () => {
    if (!emotion) {
      Alert.alert(t('common.error'), t('diary.sentiment.question'));
      return;
    }

    setIsLoading(true);
    try {
      let noteContent = noteText.trim();
      const placeholder = categoryLabel 
              ? t('diary.sentiment.placeholderWithCategory', { emotion: emotionLabel.toLowerCase(), category: categoryLabel })
              : t('diary.sentiment.placeholderNoCategory', { emotion: emotionLabel.toLowerCase() });

      // If no user input, use the placeholder content as the note
      if (!noteContent) {
        noteContent = placeholder;
      }

      const entryData = {
        emotion: emotion as string,
        category: categories as string,
        noteContent: noteContent,
        entry_date: selectedDate || new Date().toISOString().split('T')[0], // Use selected date or today's date
      };

      const result = await createEntry(entryData);

      if (result) {
        // Navigate to diary page with success parameter
        router.replace({
          pathname: '/diary',
          params: { sentimentSaved: 'true', savedEmotion: emotion }
        });
      } else {
        Alert.alert(t('common.error'), t('diary.sentiment.error'));
      }
    } catch (error) {
      console.error('Error saving sentiment entry:', error);
      Alert.alert(t('common.error'), t('diary.sentiment.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    router.back();
  };

  const emotionLabel = t(`diary.emotions.${emotion}`) || emotion.charAt(0).toUpperCase() + emotion.slice(1);
  const categoryArray = categories ? categories.split(',').map(c => c.trim()) : [];
  const categoryLabel = getCategoryLabel(categoryArray);

  return (
    <SafeAreaView style={styles.container}>
      <PageHeader 
        title={t('diary.sentiment.title')} 
        onBack={handleBack}
      />

      {/* Body */}
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.body}>
        <Text style={styles.questionText}>{t('diary.sentiment.writeMemory')}</Text>

        {/* Summary */}
        <View style={styles.summaryCard}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryEmoji}>{getEmotionEmoji(emotion)}</Text>
            <View style={styles.summaryText}>
              <Text style={styles.summaryLabel}>{emotionLabel}</Text>
              {categoryLabel && (
                <Text style={styles.summaryCategory}>{categoryLabel}</Text>
              )}
            </View>
          </View>
        </View>

        {/* Note Input */}
        <View style={styles.noteContainer}>
          <TextInput
            style={[styles.noteInput, { color: '#151515' }]}
            multiline
            placeholder={categoryLabel 
              ? t('diary.sentiment.placeholderWithCategory', { emotion: emotionLabel.toLowerCase(), category: categoryLabel })
              : t('diary.sentiment.placeholderNoCategory', { emotion: emotionLabel.toLowerCase() })
            }
            placeholderTextColor="#999999"
            value={noteText}
            onChangeText={setNoteText}
            textAlignVertical="top"
          />
        </View>
      </ScrollView>

      {/* Button Section */}
      <View style={styles.buttonSection}>
        <PrimaryButton
          title={isLoading ? 'Salvando...' : t('diary.sentiment.nextButton')}
          onPress={handleSave}
          disabled={isLoading}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  scrollView: {
    flex: 1,
  },
  body: {
    padding: 24,
    gap: 20,
  },
  questionText: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
  },
  summaryCard: {
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#F9F8F3',
    borderWidth: 1,
    borderColor: '#F0EEE4',
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  summaryEmoji: {
    fontSize: 32,
    lineHeight: 44.8,
  },
  summaryText: {
    flex: 1,
    gap: 2,
  },
  summaryLabel: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    lineHeight: 22.4,
  },
  summaryCategory: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#666666',
    lineHeight: 19.6,
  },
  noteContainer: {
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    flex: 1,
  },
  noteInput: {
    padding: 16,
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 22.4,
    flex: 1,
  },
  buttonSection: {
    padding: 24,
    backgroundColor: '#FFFFFF',
    gap: 12,
  },
});